/* eslint-disable max-lines */
import { SiteSchedule } from '@hakimo-ui/hakimo/types';
import { convertToSeconds, splitDailyWindow } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import {
  BookmarkIcon,
  PlusCircleIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { TimeInputField } from '../time-input/TimeInputField';
import { WeekdayActions } from './WeekdayActions';

interface Props {
  schedule: SiteSchedule;
  updateSchedule: (schedule: SiteSchedule) => void;
  isDeletable?: boolean;
  onDelete?: () => void;
  isDefault?: boolean;
  isEditing?: boolean;
  allowAddRange?: boolean;
}

export function ScheduleItem(props: Props) {
  const {
    schedule,
    updateSchedule,
    isDeletable,
    onDelete,
    isDefault,
    isEditing,
    allowAddRange = true,
  } = props;

  const { ranges, enabledDays } = schedule;

  const onRangeChange =
    (rangeIndex: number, field: 'startTime' | 'endTime') => (value: string) => {
      const updatedRanges = [...schedule.ranges];
      updatedRanges[rangeIndex] = {
        ...updatedRanges[rangeIndex],
        [field]: value,
      };
      updateSchedule({ ...schedule, ranges: updatedRanges });
    };

  const updateEnabledDays = (days: boolean[]) =>
    updateSchedule({ ...schedule, enabledDays: days });

  const onAddRange = () => {
    const updatedRanges = [...schedule.ranges];
    updatedRanges.push({
      startTime: '',
      endTime: '',
      order: schedule.ranges.length,
    });
    updateSchedule({ ...schedule, ranges: updatedRanges });
  };

  const onDeleteRange = (index: number) => () => {
    const updatedRanges = [...schedule.ranges];
    updatedRanges.splice(index, 1);
    updateSchedule({ ...schedule, ranges: updatedRanges });
  };

  const getExtraInfo = (range: { startTime: string; endTime: string }) => {
    const { startTime, endTime } = range;
    const segments = splitDailyWindow(startTime, endTime);
    if (!segments.length) return '';

    const isNextDay = segments.length > 1;
    const is24HrWindow =
      convertToSeconds(startTime) === convertToSeconds(endTime);

    return `${isNextDay ? '+1 Day' : ''}${is24HrWindow ? '(24 Hrs)' : ''}`;
  };

  return (
    <div
      className={clsx(
        'bg-onlight-bg-2 dark:bg-ondark-bg-2 dark:border-ondark-line-3 relative flex items-center gap-4 rounded-md border p-4 pb-2 shadow-sm transition-all duration-300 ease-in-out',
        ranges.length > 1 && 'flex-col'
      )}
    >
      {isDeletable && (
        <Button
          variant="icon"
          onClick={onDelete}
          title="Delete schedule"
          className="absolute right-0 top-0 ml-4"
        >
          <XMarkIcon className="h-5 w-5" />
        </Button>
      )}
      {isDefault && (
        <BookmarkIcon className="text-primary-500 absolute left-0 top-0 h-5 w-5" />
      )}
      <div className="flex divide-x-2">
        {ranges.map((range, i) => (
          <div className="flex items-center justify-center gap-2 px-4" key={i}>
            <div className="w-32">
              <TimeInputField
                label="Start time"
                onChange={onRangeChange(i, 'startTime')}
                value={range.startTime}
                required
                isReadOnly={!isEditing}
              />
            </div>
            <div className="relative w-32">
              <span className="text-primary-500 absolute left-16 top-1 whitespace-nowrap text-xs">
                {getExtraInfo(range)}
              </span>
              <TimeInputField
                label="End time"
                onChange={onRangeChange(i, 'endTime')}
                value={range.endTime}
                isReadOnly={!isEditing}
              />
              {i > 0 && isEditing && (
                <Button
                  variant="icon"
                  onClick={onDeleteRange(i)}
                  title="Delete range"
                  className="absolute -right-10 top-[40%]"
                >
                  <TrashIcon className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between gap-4">
        <WeekdayActions
          selectedDays={enabledDays}
          updateSelectedDays={updateEnabledDays}
          disableSelectedDays={!isDeletable}
        />
        {allowAddRange && ranges.length < 2 && (
          <Button
            variant="icon"
            onClick={onAddRange}
            title="Add range"
            className="flex flex-1 gap-1 text-sm"
          >
            <PlusCircleIcon className="h-5 w-5" />
            <span>Add range</span>
          </Button>
        )}
      </div>
    </div>
  );
}

export default ScheduleItem;
